import { FocusEvent, KeyboardEvent, useEffect, useState } from "react"

import useIsDesktop from "@hooks/useIsDesktop"

import { faIcon } from "@utils/faIcon"

import { IconButton } from "@atoms"

import SearchInput from "@molecules/SearchInput/SearchInput"

import useViewBar from "@organisms/ViewBar/ViewBar.context"
import ViewBarPortal from "@organisms/ViewBar/ViewBarPortal/ViewBarPortal"

import { ViewBarSearchButtonProps } from "./ViewBarSearchButton.types"

export default function ViewBarSearchButton(props: ViewBarSearchButtonProps) {
    const { defaultValue, onValueChange } = props

    const isDesktop = useIsDesktop()
    const [isSearchActive, setIsSearchActive] = useState(false)
    // A term already applied opens the bar without focus, so returning to the list doesn't raise the keyboard.
    const [shouldAutoFocus, setShouldAutoFocus] = useState(false)

    // The bar stays open while a term applies, wherever it came from (a restored search, or one typed in the
    // desktop field before the screen narrowed), so an active search is never hidden behind the button.
    const isSearchBarOpen = isSearchActive || defaultValue !== ""

    // The mobile search row portals into ViewBar.Root; assert that ancestor at mount rather than on the first tap.
    useViewBar()

    // The desktop field is always open, so the mobile bar starts over whenever the screen widens.
    useEffect(() => {
        if (isDesktop) {
            setIsSearchActive(false)
            setShouldAutoFocus(false)
        }
    }, [isDesktop])

    const openSearch = () => {
        setShouldAutoFocus(true)
        setIsSearchActive(true)
    }

    // The bar folds back into the button once it is left empty. The blur can beat the input's own debounce,
    // so the cleared value is committed here rather than waiting for it.
    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
        if (event.target.value === "") {
            onValueChange("")
            setIsSearchActive(false)
        }
    }

    // Escape on an empty bar leaves it, and the blur folds it back.
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Escape" && event.currentTarget.value === "") {
            event.currentTarget.blur()
        }
    }

    if (isDesktop) {
        return (
            <SearchInput type="field" defaultValue={defaultValue} onValueChange={onValueChange} aria-label="Search" />
        )
    } else if (isSearchBarOpen) {
        return (
            <ViewBarPortal>
                <SearchInput
                    type="bar"
                    defaultValue={defaultValue}
                    onValueChange={onValueChange}
                    autoFocus={shouldAutoFocus}
                    onFocus={() => setIsSearchActive(true)}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    aria-label="Search"
                />
            </ViewBarPortal>
        )
    } else {
        return (
            <IconButton
                size="lg"
                variant="ghost"
                icon={faIcon({ name: "magnifying-glass", style: "regular" })}
                onClick={openSearch}
                noDebounce={true}
                aria-label="Search"
            />
        )
    }
}
